import { IconBadge } from '@monksflow/monks-ui';
import {
  FileAudioIcon,
  FileCodeIcon,
  FileIcon,
  FileTextIcon,
  FileVideoIcon,
  ImageIcon,
} from 'lucide-react';

interface FileTypeIconProps {
  fileType?: string;
}

const ICONS = {
  pdf: FileTextIcon,
  markdown: FileCodeIcon,
  image: ImageIcon,
  video: FileVideoIcon,
  audio: FileAudioIcon,
  text: FileTextIcon,
  generic: FileIcon,
} as const;

type FileIconName = keyof typeof ICONS;

const resolveIconName = (fileType?: string): FileIconName => {
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType === 'text/markdown') return 'markdown';
  if (fileType?.startsWith('image/')) return 'image';
  if (fileType?.startsWith('video/')) return 'video';
  if (fileType?.startsWith('audio/')) return 'audio';
  if (fileType?.startsWith('text/')) return 'text';
  return 'generic';
};

/**
 * `data-file-icon` names the resolved glyph. It is the only assertable trace of
 * which icon was chosen — lucide icons render no text — and callers depend on a
 * file type mapping to a recognisable icon, so it is part of the contract.
 */
export const FileTypeIcon = ({ fileType }: FileTypeIconProps) => {
  const name = resolveIconName(fileType);
  const Icon = ICONS[name];

  return (
    <IconBadge
      className="size-9 bg-accent text-accent-foreground"
      data-testid="file-type-icon"
      data-file-icon={name}
    >
      <Icon />
    </IconBadge>
  );
};
